import { Box, IconButton, InputAdornment, Link, TextField } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import React from 'react';
import { FirebaseError } from 'firebase/app';
import {
  MultiFactorError,
  MultiFactorResolver,
  sendEmailVerification,
  signInWithEmailAndPassword,
  User,
  UserCredential
} from 'firebase/auth';
import { LoginState } from './LoginModal';
import { BodyText, CaptionText, Subheading } from '../../shared/typography/PrimaryTypography';
import PrimaryButton from '../../shared/button/PrimaryButton';
import {
  auth,
  clearRecaptchaData,
  generateMfaResolver,
  generateRecaptchaVerifier,
  getRecaptchaVerifier,
  retrieveMultiFactorResolver,
  startSmsMfaSignin
} from '../../../lib/google/firebase';
import { UserWithMfaInfo } from '../../../lib/auth/auth.util';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';

interface LoginPasswordProps {
  email: string;
  setSignInUser: (user: User | null) => void;
  setShowRecaptchaContainer: (state: boolean) => void;
  handleLoginState: (login_state: LoginState) => void;
  resetVerifyTimeout: (seconds: number) => void;
}

/**
 * Handles the password entry step of the login flow.
 * @param {(user: User | null): void} setSignInUser Sets the global user state to be used in application.
 * @param {(state: boolean): void} setShowRecaptchaContainer Controls visibility of the recaptcha container.
 * @param {(login_state: LoginState): void} handleLoginState Handles the changing of modal state to various parts of the login flow.
 * @param {(seconds: number): void} resetVerifyTimeout Resets the counter for preventing instant clicking of verification steps.
 * @returns {JSX.Element}
 */
const LoginPassword = ({
  email,
  setSignInUser,
  setShowRecaptchaContainer,
  handleLoginState,
  resetVerifyTimeout
}: LoginPasswordProps): JSX.Element => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const [password, setPassword] = React.useState<string>('');
  const [passwordFocused, setPasswordFocus] = React.useState<boolean>(false);
  const [validPassword, setValidPassword] = React.useState<boolean>(false);
  const [loginFault, setLoginFault] = React.useState<boolean>(false);
  const [loginFaultMessage, setLoginFaultMessage] = React.useState<string>('Unknown Error');
  const [signInLoad, setSignInLoad] = React.useState<boolean>(false);
  const [showPassword, setShowPassword] = React.useState<boolean>(false);

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setPassword(event.target.value);
  };

  // Handles updating of current login fault text.
  const handleLoginFault = (state: boolean, message: string): void => {
    setLoginFault(state);
    setLoginFaultMessage(message);
  };

  // Handles debounce for password input and validates if password is of required length.
  React.useEffect(() => {
    setTimeout(() => {
      setValidPassword(password.length > 0);
    }, 500);
    return () => {
      null;
    };
  }, [password, passwordFocused]);

  // Handles timeout of reclick on sign in button while recaptcha loads
  const debounceSignIn = (): (() => void) => {
    const timeout_id: NodeJS.Timeout = setTimeout(async () => {
      setSignInLoad(false);
    }, 5 * 1000);
    return () => clearTimeout(timeout_id);
  };

  // Handles sending of verification email if users accounts email is not verified.
  const handleEmailVerifySend = (): void => {
    try {
      const user: User | null = auth.currentUser;
      if (user) {
        sendEmailVerification(user).catch((err) => {
          const tracking_id: string = generateUuidV4();
          log('error', new MetadataError(err, { user: user }, tracking_id));
          setFeedbackData({
            message: `${err.message}. Tracking ID: ${tracking_id}`,
            state: true,
            type: 'error'
          });
        });
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: SignIn failed on an unknown error while calling handleEMailVerifySend.',
          { 'auth.currentUser': auth.currentUser },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Failed to send email verification. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  // Handler for firebase login auth results and errors.
  const onLogin = (event: React.MouseEvent<HTMLButtonElement>): void => {
    clearRecaptchaData();
    event.preventDefault();
    setSignInLoad(true);
    setShowRecaptchaContainer(true);
    try {
      signInWithEmailAndPassword(auth, email, password)
        .then((userCredential: UserCredential) => {
          // Signed in
          setSignInUser(auth.currentUser);
          const user: UserWithMfaInfo = userCredential.user;
          if (user.emailVerified) {
            handleLoginState('mfa_selection');
          } else {
            handleEmailVerifySend();
            resetVerifyTimeout(60);
            handleLoginState('verify_email');
          }
        })
        .catch(async (err: unknown) => {
          if ((err as FirebaseError).code == 'auth/multi-factor-auth-required') {
            resetVerifyTimeout(30);
            generateMfaResolver(err as MultiFactorError);
            if (retrieveMultiFactorResolver()?.hints[0].factorId === 'totp') {
              handleLoginState('verify_authenticator');
            } else {
              const resolver: MultiFactorResolver = generateMfaResolver(err as MultiFactorError);
              debounceSignIn();
              await startSmsMfaSignin(
                resolver.hints[0],
                resolver.session,
                getRecaptchaVerifier() ?? generateRecaptchaVerifier()
              )
                .then(() => handleLoginState('verify_mfa'))
                .catch((err: unknown) => {
                  const tracking_id: string = generateUuidV4();
                  log(
                    'error',
                    new MetadataError(
                      err instanceof Error
                        ? err.message
                        : 'Error: SignIn failed on an unknown error while calling startSmsMfaSignin.',
                      { 'auth.currentUser': auth.currentUser },
                      tracking_id
                    )
                  );
                  setFeedbackData({
                    message: `${(err as Error).message}. Tracking ID: ${tracking_id}`,
                    state: true,
                    type: 'error'
                  });
                });
            }

            setSignInLoad(false);
          } else if ((err as FirebaseError).code == 'mfa-not-activated') {
            // Handle the user not having MFA on their account
            handleLoginFault(true, 'User requires MFA activation');
            setSignInLoad(false);
          } else if ((err as FirebaseError).code == 'auth/too-many-requests') {
            // Handle brute force attacks
            handleLoginFault(
              true,
              'To many failed sign in requests, account blocked temporarily, please try again later.'
            );
            setSignInLoad(false);
          } else if (
            (err as FirebaseError).code === 'auth/user-not-found' ||
            (err as FirebaseError).code === 'auth/invalid-email' ||
            (err as FirebaseError).code === 'auth/user-disabled' ||
            (err as FirebaseError).code === 'auth/invalid-credential' ||
            (err as FirebaseError).code == 'auth/wrong-password'
          ) {
            handleLoginFault(true, 'Invalid Email or Password please try again.');
            setSignInLoad(false);
          } else if ((err as FirebaseError).code === 'auth/missing-verification-id') {
            log(
              'error',
              new MetadataError(
                (err as FirebaseError).code,
                { 'auth.currentUser': auth.currentUser },
                generateUuidV4()
              )
            );
            handleLoginFault(true, 'Recaptcha Expired, please log in again.');
            setSignInLoad(false);
          } else {
            //Catch any errors that don't require specific handling
            const tracking_id: string = generateUuidV4();
            log(
              'error',
              new MetadataError(
                (err as FirebaseError).code,
                { 'auth.currentUser': auth.currentUser },
                tracking_id
              )
            );
            setFeedbackData({
              message: `${(err as FirebaseError).code}. Tracking ID: ${tracking_id}`,
              state: true,
              type: 'error'
            });
          }
        });
    } catch (error: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          error instanceof Error
            ? error.message
            : 'Error: SignIn failed on an unknown error while calling onLogin.',
          { 'auth.currentUser': auth.currentUser },
          tracking_id
        )
      );
    }
  };

  return (
    <Box>
      <Subheading weight="Medium" color="primary" sx={{ textAlign: 'center', mb: 4 }}>
        Enter Your Password
      </Subheading>
      <BodyText sx={{ textAlign: 'center', my: 2 }}>Enter your password for Syntiro</BodyText>
      <form>
        <TextField
          fullWidth
          variant="outlined"
          value={email}
          sx={{ my: 1 }}
          slotProps={{ htmlInput: { autoComplete: 'username' } }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Link
                  href="#"
                  onClick={() => handleLoginState('login_identifier')}
                  sx={{ color: 'primary.main', fontWeight: 500, textDecoration: 'none' }}
                >
                  Edit
                </Link>
              </InputAdornment>
            )
          }}
        />
        <TextField
          error={!validPassword && passwordFocused}
          onBlur={() => setPasswordFocus(true)}
          data-testid="password-input"
          id="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          variant="outlined"
          onInput={handlePasswordChange}
          value={password}
          sx={{ my: 2 }}
          slotProps={{ htmlInput: { autoComplete: 'current-password' } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        {loginFault && (
          <CaptionText
            sx={{
              mb: 2,
              color: loginFaultMessage.includes('link sent') ? '#01579B' : 'red'
            }}
          >
            {loginFaultMessage}
          </CaptionText>
        )}
        <Box sx={{ mt: 1 }}>
          <Link
            href="#"
            data-testid="recovery-button"
            id="recovery-button"
            onClick={() => handleLoginState('recovery')}
            sx={{ color: '#053d5d' }}
          >
            Forgot password?
          </Link>
        </Box>
        <PrimaryButton
          data-testid="sign-in-button"
          disabled={!validPassword}
          loading={signInLoad}
          sx={{ mt: 4, width: '100%' }}
          onClick={onLogin}
          id="sign-in-button"
          type="submit"
        >
          CONTINUE
        </PrimaryButton>
      </form>
    </Box>
  );
};

export default LoginPassword;
